import { useMemo } from "react";
import { Part, TextureScoringContext } from "../types";

const commonWords = new Set(["the", "and", "or", "for", "with", "from"]);

export function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  if (s1 === s2) return 1.0;
  if (s1.length === 0 || s2.length === 0) return 0.0;

  const matrix = Array(s2.length + 1)
    .fill(null)
    .map(() => Array(s1.length + 1).fill(null));

  for (let i = 0; i <= s1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= s2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= s2.length; j++) {
    for (let i = 1; i <= s1.length; i++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(matrix[j][i - 1] + 1, matrix[j - 1][i] + 1, matrix[j - 1][i - 1] + cost);
    }
  }

  const maxLength = Math.max(s1.length, s2.length);
  return maxLength === 0 ? 1.0 : (maxLength - matrix[s2.length][s1.length]) / maxLength;
}

export function scoreVariantForTexture(
  variant: { id: string; displayName: string },
  context: TextureScoringContext | null,
): number {
  if (!context) return 0;
  const { selectedTextureName, selectedUniqueWords, selectedUniqueText } = context;
  let score = 0;
  const displayName = variant.displayName.toLowerCase().trim();
  const displayNameWords = displayName
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/g, ""))
    .filter((word) => word.length > 2 && !commonWords.has(word));

  if (selectedUniqueWords.length > 0) {
    let exactMatches = 0;
    selectedUniqueWords.forEach((uniqueWord) => {
      if (displayNameWords.includes(uniqueWord)) exactMatches++;
    });
    if (exactMatches > 0) {
      const matchRatio = exactMatches / selectedUniqueWords.length;
      score = Math.max(score, 0.7 + matchRatio * 0.25);
    }
    const fuzzyScore = calculateSimilarity(selectedUniqueText, displayName);
    score = Math.max(score, fuzzyScore * 0.6);
  } else {
    const nameScore = calculateSimilarity(selectedTextureName, displayName);
    score = Math.max(score, nameScore);
  }

  selectedUniqueWords.forEach((uniqueWord) => {
    if (displayName.includes(uniqueWord)) score = Math.max(score, 0.5);
  });

  const variantId = variant.id.toLowerCase().trim();
  if (selectedTextureName.includes(variantId) || variantId.includes(selectedTextureName)) {
    score = Math.max(score, selectedTextureName === variantId ? 0.95 : 0.7);
  } else {
    const idParts = variantId.split(/[:_-]/).filter((part) => part.length > 0);
    for (const part of idParts) {
      if (selectedTextureName.includes(part) || part.includes(selectedTextureName)) {
        score = Math.max(score, 0.6);
        break;
      }
    }
  }
  return score;
}

export function buildTextureScoringContext(part: Part, textureIndex: number): TextureScoringContext | null {
  const selectedTexture = part.textures[textureIndex];
  if (!selectedTexture) return null;

  const allTextureNames = part.textures.map((t) => t.name.toLowerCase().trim());
  const selectedTextureName = selectedTexture.name.toLowerCase().trim();
  const totalTextures = allTextureNames.length;
  const wordToTextures = new Map<string, Set<number>>();

  allTextureNames.forEach((name, idx) => {
    const words = name
      .split(/\s+/)
      .map((word) => word.replace(/[^a-z0-9]/g, ""))
      .filter((word) => word.length > 2 && !commonWords.has(word));
    words.forEach((word) => {
      if (!wordToTextures.has(word)) wordToTextures.set(word, new Set());
      wordToTextures.get(word)!.add(idx);
    });
  });

  const threshold = Math.max(1, Math.floor(totalTextures * 0.25));
  const uniqueWords = new Set<string>();
  wordToTextures.forEach((textureIndices, word) => {
    if (textureIndices.size <= threshold) uniqueWords.add(word);
  });

  const selectedTextureWords = selectedTextureName
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/g, ""))
    .filter((word) => word.length > 2 && !commonWords.has(word));
  const selectedUniqueWords = selectedTextureWords.filter((word) => uniqueWords.has(word));
  const selectedUniqueText = selectedUniqueWords.join(" ");

  return { selectedTextureName, selectedUniqueWords, selectedUniqueText };
}

export function useTextureScoringContext(
  selectedPart: Part | undefined,
  selectedTextureIndex: number | null,
): TextureScoringContext | null {
  return useMemo(() => {
    if (selectedTextureIndex === null || !selectedPart) return null;
    return buildTextureScoringContext(selectedPart, selectedTextureIndex);
  }, [selectedTextureIndex, selectedPart]);
}

function removeThreeDigitNumbers(locationName: string): string {
  return locationName.replace(/\s+\d{3}\b/g, "");
}

export function findBestLocationMatch(
  sourceLocationName: string,
  availableLocations: Array<{ id: string; name: string }>,
): { id: string; name: string } | null {
  if (!sourceLocationName || availableLocations.length === 0) return null;

  const cleanSourceName = removeThreeDigitNumbers(sourceLocationName);
  let bestMatch: { id: string; name: string } | null = null;
  let bestScore = 0;

  for (const location of availableLocations) {
    const cleanTargetName = removeThreeDigitNumbers(location.name);
    const score = calculateSimilarity(cleanSourceName, cleanTargetName);
    if (score > bestScore && score > 0.5) {
      bestScore = score;
      bestMatch = location;
    }
  }

  return bestMatch;
}

export function findSimilarlyNamedTextures(
  sourceTextures: Array<{ name: string; materialVariantIds?: string[] }>,
  targetTextures: Array<{ name: string; index: number }>,
  minSimilarity = 0.5,
): Array<{ sourceIndex: number; targetIndex: number }> {
  const matches: Array<{ sourceIndex: number; targetIndex: number }> = [];
  const sourceWithMaterials = sourceTextures
    .map((t, i) => ({ ...t, originalIndex: i }))
    .filter((t) => t.materialVariantIds && t.materialVariantIds.length > 0);
  if (sourceWithMaterials.length === 0 || targetTextures.length === 0) return matches;

  const usedTargetIndices = new Set<number>();
  for (const source of sourceWithMaterials) {
    let bestIdx = -1;
    let bestScore = minSimilarity;
    const sName = source.name.trim().toLowerCase();
    targetTextures.forEach((target, idx) => {
      if (usedTargetIndices.has(idx)) return;
      const score = calculateSimilarity(sName, target.name.trim().toLowerCase());
      if (score > bestScore) {
        bestScore = score;
        bestIdx = idx;
      }
    });
    if (bestIdx >= 0) {
      usedTargetIndices.add(bestIdx);
      matches.push({ sourceIndex: source.originalIndex, targetIndex: bestIdx });
    }
  }
  return matches;
}
